import React, { createContext, useContext, useMemo } from "react";
import CssBaseline from "@material-ui/core/CssBaseline";
import { injectIntl } from "react-intl";
import { useAuthDataContext } from "components/auth";
import MuiPickersProvider from "./mui-pickers-provider";
import MuiThemeProvider from "./mui-theme-provider";
import { cinfoPageUrl, tiiviiLogo, cinfoLogo, getHelpUrl } from "config/paths";
import mergeWith from "lodash/mergeWith";
import merge from "lodash/merge";

const customizeMerge = (a, b) => {
  if (!a) {
    return b;
  }

  if (!b) {
    return a;
  }

  if (typeof a === "string" || typeof b === "string") {
    return b;
  }

  return merge(a, b);
};

const parseLocale = userLocale => {
  const locale = userLocale
    ? userLocale
        .split("_")[0]
        .trim()
        .toLowerCase()
    : "";

  return ["es", "gl", "ca"].includes(locale) ? "es" : "en";
};

const getDefaultTheme = ({ intl, locale }) => ({
  uiTheme: {
    primaryColor: {
      light: "#e0e0e0",
      main: "#1d1d1b",
      dark: "black",
    },
    secondaryColor: {
      light: "#e406131c",
      main: "#e30613",
      dark: "#b11c25",
    },
  },
  headerData: {
    logo: {
      src: tiiviiLogo,
      alt: intl.formatMessage({ id: "app.name" }),
      link: "/",
    },
  },
  footerData: {
    logo: {
      src: cinfoLogo,
      alt: intl.formatMessage({ id: "app.owner" }),
      link: cinfoPageUrl,
    },
    copyrightName: intl.formatMessage({ id: "app.owner" }),
    privacyPolicy: cinfoPageUrl + intl.formatMessage({ id: "app.privacy.url" }),
    useTerms: cinfoPageUrl + intl.formatMessage({ id: "app.terms.url" }),
  },
  helpUrl: getHelpUrl(parseLocale(locale)),
  favicon: "/static/favicon.ico",
});

const CustomThemeContext = createContext(null);

const CustomThemeWithoutIntlProvider = ({ children, intl }) => {
  const { userTheme, user, locale } = useAuthDataContext();
  const defaultTheme = getDefaultTheme({ intl, locale: user ? user.locale : locale });
  const customTheme = useMemo(
    () => (userTheme ? mergeWith({}, defaultTheme, userTheme, customizeMerge) : defaultTheme),
    [defaultTheme, userTheme],
  );

  return (
    <CustomThemeContext.Provider value={{ customTheme }}>
      <MuiPickersProvider>
        <MuiThemeProvider>
          <CssBaseline />
          {children}
        </MuiThemeProvider>
      </MuiPickersProvider>
    </CustomThemeContext.Provider>
  );
};

const CustomThemeProvider = injectIntl(CustomThemeWithoutIntlProvider);

const useCustomThemeContext = () => {
  const context = useContext(CustomThemeContext);
  if (context === undefined) {
    throw new Error("useCustomThemeContext must be used within a CustomThemeProvider");
  }
  return context;
};

export { CustomThemeProvider, useCustomThemeContext };
